import ConfirmationModal from 'components/ConfirmationModal';
import CustomWalkthroughTooltip from 'components/CustomWalkthroughTooltip';
import { IDataEntryModal } from 'interfaces/components/dataEntryModal';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import ReactJoyride, { ACTIONS, CallBackProps, STATUS } from 'react-joyride';
import 'react-toastify/dist/ReactToastify.css';
import { useAppDispatch, useAppSelector } from 'store';
import { resetCompanyInfo } from 'store/companyInfo/companyInfo.slice';
import {
  companyStatus,
  modalModes,
  modalSections,
  modalTabs
} from 'utils/constants';
import isEmail from 'validator/es/lib/isEmail';
import CustomModal from 'components/CustomModal';
import CalculateModal from 'components/CalculateModal';
import { calculateModalConfigs } from 'utils/constants/calculateModalConfig';
import {
  CalculateFieldNames,
  stateFieldMappings
} from 'utils/constants/fieldNames';
import {
  CalculateFieldValue,
  CalculateModalConfig,
  CalculateStatesAndHandlers
} from 'interfaces/constants/CalculateModalConfig';
import {
  calculateOPEXDollar,
  calculateOPEXPercent,
  calculateHeadcountPercent,
  formatCalculationResult
} from 'utils/helpers/calculateFunctions';
import {
  dataModalWalkthroughSteps,
  dataModalWalkthroughStepsNonPortco
} from 'utils/constants/tutorials-walkthrough';
import { useSubmitData, ISubmitData } from 'hooks/useSubmitData';
import { useAuth } from 'context/AuthContext';
import { applySpotlightStyles } from 'utils/helpers/walkthroughHelpers';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import RequiredInfoSection from './sections/RequiredInfoSection';
import GrowthAndRetentionSection from './sections/GrowthAndRetentionSection';
import OperationalEfficiencySection from './sections/OperationalEfficiencySection';
import HeadcountSection from './sections/HeadcountSection';
import CompanyDetailsSection from './sections/CompanyDetailsSection';
import SavedUserData from './SavedUserData';
import SavedDataDetails from 'components/SavedDataDetails';
import { resetChartForms } from 'store/chartForms/chartForms.slice';
import { UserHistoricalFormDataItem } from 'interfaces/components/savedUserData/UserHistoricalFormDataItem';
import { getHistoricalCompsData } from 'network/getUserHistoricalFormData';

const DataEntryModal = ({
  isOpen,
  onClose,
  mode,
  onGrowthEnterpriseReset,
  updateDependentFields
}: IDataEntryModal) => {
  const isFirstRender = useRef(true);
  const [isCalculateModalOpen, setIsCalculateModalOpen] = useState(false);
  const [selectedTabs, setSelectedTabs] = useState<modalTabs>(modalTabs.FORM);
  const [modalConfig, setModalConfig] = useState<CalculateModalConfig>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState(modalSections.COMPANY_DETAILS);
  const [selectedSavedData, setSelectedSavedData] =
    useState<UserHistoricalFormDataItem | null>(null);

  const { user, isAuthenticated, isPortCo } = useAuth();
  const { submitData, isSubmitting } = useSubmitData(isAuthenticated);

  const walkthroughSteps = !isAuthenticated
    ? dataModalWalkthroughSteps
    : !isPortCo
    ? dataModalWalkthroughStepsNonPortco
    : dataModalWalkthroughSteps;

  const companyInfo = useAppSelector(state => state.companyInfo);
  const { softwareSector, growthMotion, primaryCustomer } = useAppSelector(
    state => state.chartFilters
  );

  const requiredInfoRef = useRef<HTMLDivElement>(null);
  const growthAndRetentionRef = useRef<HTMLDivElement>(null);
  const operationalEfficiencyRef = useRef<HTMLDivElement>(null);
  const headcountRef = useRef<HTMLDivElement>(null);
  const companyDetailsRef = useRef<HTMLDivElement>(null);
  const refsMapper = {
    [modalSections.REQUIRED_INFORMATION]: requiredInfoRef,
    [modalSections.GROWTH_AND_RETENTION]: growthAndRetentionRef,
    [modalSections.OPERATIONAL_EFFICIENCY]: operationalEfficiencyRef,
    [modalSections.HEADCOUNT]: headcountRef,
    [modalSections.COMPANY_DETAILS]: companyDetailsRef
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [requiredInfoParentRef, requiredInfoInView] = useInView({
    threshold: 0.75
  });
  const [growthAndRetentionParentRef, growthAndRetentionInView] = useInView({
    threshold: 1
  });
  const [operationalEfficiencyParentRef, operationalEfficiencyInView] =
    useInView({
      threshold: 0.75
    });
  const [headcountParentRef, headcountInView] = useInView({
    threshold: 1
  });
  const [companyDetailsParentRef, companyDetailsInView] = useInView({
    threshold: 1
  });

  const [currentCompanyStatus, setCompanyStatus] =
    useState<companyStatus | null>(null);
  const [currentARR, setCurrentARR] = useState<string | null>(null);
  const [revenue, setRevenue] = useState<string | null>(null);
  const [fcfMargin, setFcfMargin] = useState<string | null>(null);
  const [avgDealSize, setAvgDealSize] = useState<string | null>(null);
  const [totalHeadcount, setTotalHeadcount] = useState<string | null>(null);
  const [grossMargin, setGrossMargin] = useState<string | null>(null);
  const [yoyARR, setYoyARR] = useState<string | null>(null);
  const [arrPerFTE, setArrPerFTE] = useState<string | null>(null);
  const [ruleOf40, setRuleOf40] = useState<string | null>(null);
  const [newLogoArrOfGrossNew, setNewLogoArrOfGrossNew] = useState<
    string | null
  >(null);
  const [expansionArrOfGrossNew, setExpansionArrOfGrossNew] = useState<
    string | null
  >(null);
  const [quickRatio, setQuickRatio] = useState<string | null>(null);
  const [netDollarRetention, setNetDollarRetention] = useState<string | null>(
    null
  );
  const [grossDollarRetention, setGrossDollarRetention] = useState<
    string | null
  >(null);
  const [newLogoVelocity, setNewLogoVelocity] = useState<string | null>(null);
  const [burnMultiple, setBurnMultiple] = useState<string | null>(null);
  const [netMagicNumber, setNetMagicNumber] = useState<string | null>(null);
  const [fcf, setFCF] = useState<string | null>(null);
  const [rD, setRD] = useState<string | null>(null);
  const [rdPercent, setRDPercent] = useState<string | null>(null);
  const [gA, setGA] = useState<string | null>(null);
  const [gaPercent, setGAPercent] = useState<string | null>(null);
  const [sM, setSM] = useState<string | null>(null);
  const [smPercent, setSMPercent] = useState<string | null>(null);
  const [CACPaybackPeriod, setCACPaybackPeriod] = useState<string | null>(null);
  const [opexPerFTE, setOpexPerFTE] = useState<string | null>(null);
  const [gAHead, setGAHead] = useState<string | null>(null);
  const [gAHeadPercent, setGAHeadPercent] = useState<string | null>(null);
  const [sMHead, setSMHead] = useState<string | null>(null);
  const [sMHeadPercent, setSMHeadPercent] = useState<string | null>(null);
  const [rDHead, setRDHead] = useState<string | null>(null);
  const [rDHeadPercent, setRDHeadPercent] = useState<string | null>(null);
  const [newLogoArr, setNewLogoArr] = useState<string | null>(null);
  const [expansionArr, setExpansionArr] = useState<string | null>(null);
  const [grossChurnArr, setGrossChurnArr] = useState<string | null>(null);
  const [BOPArr, setBOPArr] = useState<string | null>(null);
  const [eopArr, setEOPArr] = useState<string | null>(null);
  const [netNewArr, setNetNewArr] = useState<string | null>(null);
  const [smOPEX, setSMOPEX] = useState<string | null>(null);
  const [totalOPEX, setTotalOPEX] = useState<string | null>(null);
  const [invalidFileSize, setInvalidFileSize] = useState<boolean>(false);
  const [companyLogoURL, setCompanyLogoURL] = useState<string | null>(null);
  const [color, setColor] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [userHistoricalFormData, setUserHistoricalFormData] = useState<
    UserHistoricalFormDataItem[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [scrollDisabled, setScrollDisabled] = useState(false);

  const onCompanyStatusChange = (newStatus: companyStatus) => {
    setCompanyStatus(newStatus);
  };

  const onCurrentArrChange = (value: string) => {
    setCurrentARR(value);
  };

  const onRevenueChange = (value: string) => {
    setRevenue(value);

    if (value) {
      if (rdPercent) setRD(calculateOPEXDollar(rdPercent, value));
      else if (rD) setRDPercent(calculateOPEXPercent(rD, value));
      if (gaPercent) setGA(calculateOPEXDollar(gaPercent, value));
      else if (gA) setGAPercent(calculateOPEXPercent(gA, value));
      if (smPercent) setSM(calculateOPEXDollar(smPercent, value));
      else if (sM) setSMPercent(calculateOPEXPercent(sM, value));

      if (value !== '0') {
        const localTotalOpex =
          Number(rD || calculateOPEXDollar(rdPercent || '0', value) || 0) +
          Number(gA || calculateOPEXDollar(gaPercent || '0', value) || 0) +
          Number(sM || calculateOPEXDollar(smPercent || '0', value) || 0);

        setTotalOPEX(String(localTotalOpex));

        if (localTotalOpex && totalHeadcount && totalHeadcount !== '0') {
          setOpexPerFTE(
            String(
              formatCalculationResult(
                Number(localTotalOpex) / Number(totalHeadcount)
              )
            )
          );
        } else {
          setOpexPerFTE(null);
        }
      }
    } else {
      setRD(null);
      setGA(null);
      setSM(null);
      setOpexPerFTE(null);
    }
  };

  const onTotalHeadcountChange = (value: string) => {
    setTotalHeadcount(value);
    setGAHead(null);
    setSMHead(null);
    setRDHead(null);
    setGAHeadPercent(null);
    setSMHeadPercent(null);
    setRDHeadPercent(null);

    if (totalOPEX && value && value !== '0') {
      setOpexPerFTE(String(Number(totalOPEX) / Number(value)));
    }

    if (gA && sM && rD && value && value !== '0') {
      const localTotalOpex = Number(value) + Number(gA || 0) + Number(sM || 0);

      setOpexPerFTE(
        String(formatCalculationResult(Number(localTotalOpex) / Number(value)))
      );
    } else {
      setOpexPerFTE(null);
    }
  };

  const onFcfMarginChange = (value: string) => {
    setFcfMargin(value);
  };

  const onAvgDealSizeChange = (value: string) => {
    setAvgDealSize(value);
  };

  const onYoyARRChange = (value: string) => {
    setYoyARR(value);
  };

  const onGrossMarginChange = (value: string) => {
    setGrossMargin(value);
  };

  const onArrPerFTEChange = (value: string) => {
    setArrPerFTE(value);
  };

  const onRuleOf40Change = (value: string) => {
    setRuleOf40(value);
  };

  const onNewLogoArrOfGrossNewChange = (value: string) => {
    setNewLogoArrOfGrossNew(value);
    if (value) {
      const expansionArrOfGrossNew = (100 - Number(value)).toString();
      setExpansionArrOfGrossNew(expansionArrOfGrossNew);
    } else {
      setExpansionArrOfGrossNew(null);
    }
  };

  const onExpansionArrOfGrossNewChange = (value: string) => {
    setExpansionArrOfGrossNew(value);
    if (value) {
      const newLogoArrOfGrossNew = (100 - Number(value)).toString();
      setNewLogoArrOfGrossNew(newLogoArrOfGrossNew);
    } else {
      setNewLogoArrOfGrossNew(null);
    }
  };

  const onQuickRatioChange = (value: string) => {
    setQuickRatio(value);
  };

  const onNetDollarRetentionChange = (value: string) => {
    setNetDollarRetention(value);
  };

  const onGrossDollarRetentionChange = (value: string) => {
    setGrossDollarRetention(value);
  };

  const onNewLogoVelocityChange = (value: string) => {
    setNewLogoVelocity(value);
  };

  const onBurnMultipleChange = (value: string) => {
    setBurnMultiple(value);
  };

  const onNetMagicNumberChange = (value: string) => {
    setNetMagicNumber(value);
  };

  const onFCFChange = (value: string) => {
    setFCF(value);
  };

  const onRDChange = (value: number | number[] | string) => {
    if (typeof value === 'number') {
      setRD(String(value));
    } else if (typeof value === 'string') {
      setRD(value);
    }

    if (revenue) {
      const percent = calculateOPEXPercent(value.toString(), revenue);
      setRDPercent(percent);
    }

    const localTotalOpex =
      Number(value || 0) + Number(gA || 0) + Number(sM || 0);

    setTotalOPEX(String(localTotalOpex));

    if (localTotalOpex && totalHeadcount && totalHeadcount !== '0') {
      setOpexPerFTE(
        String(
          formatCalculationResult(
            Number(localTotalOpex) / Number(totalHeadcount)
          )
        )
      );
    } else {
      setOpexPerFTE(null);
    }
  };

  const onRDPercentChange = (value: string) => {
    setRDPercent(value);
    if (revenue) {
      setRD(calculateOPEXDollar(value, revenue));

      const localTotalOpex =
        Number(calculateOPEXDollar(value, revenue) || 0) +
        Number(gA || 0) +
        Number(sM || 0);

      setTotalOPEX(String(localTotalOpex));

      if (localTotalOpex && totalHeadcount && totalHeadcount !== '0') {
        setOpexPerFTE(
          String(
            formatCalculationResult(
              Number(localTotalOpex) / Number(totalHeadcount)
            )
          )
        );
      } else {
        setOpexPerFTE(null);
      }
    }
  };

  const onGAChange = (value: number | number[] | string) => {
    if (typeof value === 'number') {
      setGA(String(value));
    } else if (typeof value === 'string') {
      setGA(value);
    }

    if (revenue) {
      const percent = calculateOPEXPercent(value.toString(), revenue);
      setGAPercent(percent);
    }

    const localTotalOpex =
      Number(value || 0) + Number(sM || 0) + Number(rD || 0);

    setTotalOPEX(String(localTotalOpex));

    if (localTotalOpex && totalHeadcount && totalHeadcount !== '0') {
      setOpexPerFTE(
        String(
          formatCalculationResult(
            Number(localTotalOpex) / Number(totalHeadcount)
          )
        )
      );
    } else {
      setOpexPerFTE(null);
    }
  };

  const onGAPercentChange = (value: string) => {
    setGAPercent(value);
    if (revenue) {
      setGA(calculateOPEXDollar(value, revenue));

      const localTotalOpex =
        Number(calculateOPEXDollar(value, revenue) || 0) +
        Number(sM || 0) +
        Number(rD || 0);

      setTotalOPEX(String(localTotalOpex));

      if (localTotalOpex && totalHeadcount && totalHeadcount !== '0') {
        setOpexPerFTE(
          String(
            formatCalculationResult(
              Number(localTotalOpex) / Number(totalHeadcount)
            )
          )
        );
      } else {
        setOpexPerFTE(null);
      }
    }
  };

  const onSMChange = (value: number | number[] | string) => {
    if (typeof value === 'number') {
      setSM(String(value));
    } else if (typeof value === 'string') {
      setSM(value);
    }

    if (revenue) {
      const percent = calculateOPEXPercent(value.toString(), revenue);
      setSMPercent(percent);
    }

    const localTotalOpex =
      Number(value || 0) + Number(gA || 0) + Number(rD || 0);

    setTotalOPEX(String(localTotalOpex));

    if (localTotalOpex && totalHeadcount && totalHeadcount !== '0') {
      setOpexPerFTE(
        String(
          formatCalculationResult(
            Number(localTotalOpex) / Number(totalHeadcount)
          )
        )
      );
    } else {
      setOpexPerFTE(null);
    }
  };

  const onSMPercentChange = (value: string) => {
    setSMPercent(value);
    if (revenue) {
      setSM(calculateOPEXDollar(value, revenue));

      const localTotalOpex =
        Number(calculateOPEXDollar(value, revenue) || 0) +
        Number(gA || 0) +
        Number(rD || 0);

      setTotalOPEX(String(localTotalOpex));

      if (localTotalOpex && totalHeadcount && totalHeadcount !== '0') {
        setOpexPerFTE(
          String(
            formatCalculationResult(
              Number(localTotalOpex) / Number(totalHeadcount)
            )
          )
        );
      } else {
        setOpexPerFTE(null);
      }
    }
  };

  const onCACPaybackPeriodChange = (value: string) => {
    setCACPaybackPeriod(value);
  };

  const onOpexPerFTEChange = (value: string) => {
    setOpexPerFTE(value);
  };

  const onGAHeadChange = (value: string) => {
    if (
      !Number(totalHeadcount) ||
      Number(value) + Number(sMHead) + Number(rDHead) > Number(totalHeadcount)
    ) {
      const adjustedValue = String(
        Number(totalHeadcount) - Number(sMHead) - Number(rDHead)
      );
      setGAHead(adjustedValue);
      setGAHeadPercent(
        calculateHeadcountPercent(adjustedValue, totalHeadcount || '0')
      );
    } else {
      setGAHead(value);
      setGAHeadPercent(calculateHeadcountPercent(value, totalHeadcount || '0'));
    }
  };

  const onGAHeadPercentChange = (value: string) => {
    setGAHeadPercent(value);
  };

  const onSMHeadChange = (value: string) => {
    if (
      !Number(totalHeadcount) ||
      Number(value) + Number(gAHead) + Number(rDHead) > Number(totalHeadcount)
    ) {
      const adjustedValue = String(
        Number(totalHeadcount) - Number(gAHead) - Number(rDHead)
      );
      setSMHead(adjustedValue);
      setSMHeadPercent(
        calculateHeadcountPercent(adjustedValue, totalHeadcount || '0')
      );
    } else {
      setSMHead(value);
      setSMHeadPercent(calculateHeadcountPercent(value, totalHeadcount || '0'));
    }
  };

  const onSMHeadPercentChange = (value: string) => {
    setSMHeadPercent(value);
  };

  const onRDHeadChange = (value: string) => {
    if (
      !Number(totalHeadcount) ||
      Number(value) + Number(gAHead) + Number(sMHead) > Number(totalHeadcount)
    ) {
      const adjustedValue = String(
        Number(totalHeadcount) - Number(gAHead) - Number(sMHead)
      );
      setRDHead(adjustedValue);
      setRDHeadPercent(
        calculateHeadcountPercent(adjustedValue, totalHeadcount || '0')
      );
    } else {
      setRDHead(value);
      setRDHeadPercent(calculateHeadcountPercent(value, totalHeadcount || '0'));
    }
  };

  const onRDHeadPercentChange = (value: string) => {
    setRDHeadPercent(value);
  };

  const onImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const reader = new FileReader();
    const file = e.target.files[0];
    const validSize = file.size <= 1e6;

    if (validSize) {
      setInvalidFileSize(false);
      reader.onloadend = () => {
        setCompanyLogoURL(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setInvalidFileSize(true);
    }
  };

  const onColorChange = (value: string) => {
    setColor(value);
  };

  const onCompanyWebsiteChange = (value: string) => {
    setCompanyWebsite(value);
  };

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const onSectionSelect = (selectedSection: modalSections) => {
    refsMapper[selectedSection].current?.scrollIntoView();
    setSelected(selectedSection);
  };

  const onNewLogoArrChange = (value: string) => {
    setNewLogoArr(value);
  };

  const onExpansionArrChange = (value: string) => {
    setExpansionArr(value);
  };

  const onGrossChurnArrChange = (value: string) => {
    setGrossChurnArr(value);
  };

  const onBOPArrChange = (value: string) => {
    setBOPArr(value);
  };

  const onEOPArrChange = (value: string) => {
    setEOPArr(value);
  };

  const onNetNewArrChange = (value: string) => {
    setNetNewArr(value);
  };

  const onSMOPEXChange = (value: string) => {
    setSMOPEX(value);
  };

  const onTotalOPEXChange = (value: string) => {
    setTotalOPEX(value);

    if (totalHeadcount && totalHeadcount !== '0') {
      setOpexPerFTE(String(Number(value) / Number(totalHeadcount)));
    }
  };

  const handleImageUploadInputClick = () => fileInputRef.current?.click();

  const calculateStatesAndHandlers: CalculateStatesAndHandlers = {
    states: {
      [CalculateFieldNames.ARR_PER_FTE]: arrPerFTE,
      [CalculateFieldNames.CURRENT_ARR]: currentARR,
      [CalculateFieldNames.TOTAL_HEADCOUNT]: totalHeadcount,
      [CalculateFieldNames.RULE_OF_40]: ruleOf40,
      [CalculateFieldNames.FCF_MARGIN]: fcfMargin,
      [CalculateFieldNames.AVG_DEAL_SIZE]: avgDealSize,
      [CalculateFieldNames.YOY_ARR]: yoyARR,
      [CalculateFieldNames.NEW_LOGO_ARR_OF_GROSS_NEW]: newLogoArrOfGrossNew,
      [CalculateFieldNames.EXPANSION_ARR_OF_GROSS_NEW]: expansionArrOfGrossNew,
      [CalculateFieldNames.NEW_LOGO_ARR]: newLogoArr,
      [CalculateFieldNames.EXPANSION_ARR]: expansionArr,
      [CalculateFieldNames.QUICK_RATIO]: quickRatio,
      [CalculateFieldNames.GROSS_CHURN_ARR]: grossChurnArr,
      [CalculateFieldNames.NET_DOLLAR_RETENTION]: netDollarRetention,
      [CalculateFieldNames.BOP_ARR]: BOPArr,
      [CalculateFieldNames.EOP_ARR]: eopArr,
      [CalculateFieldNames.NET_NEW_ARR]: netNewArr,
      [CalculateFieldNames.GROSS_DOLLAR_RETENTION]: grossDollarRetention,
      [CalculateFieldNames.BURN_MULTIPLE]: burnMultiple,
      [CalculateFieldNames.NET_MAGIC_NUMBER]: netMagicNumber,
      [CalculateFieldNames.FCF]: fcf,
      [CalculateFieldNames.SM_OPEX]: smOPEX,
      [CalculateFieldNames.OPEX_PER_FTE]: opexPerFTE,
      [CalculateFieldNames.TOTAL_OPEX]: totalOPEX,
      [CalculateFieldNames.RD_HEADCOUNT]: rDHead,
      [CalculateFieldNames.GA_HEADCOUNT]: gAHead,
      [CalculateFieldNames.SM_HEADCOUNT]: sMHead,
      [CalculateFieldNames.GA_HEADCOUNT_PERCENT]: gAHeadPercent,
      [CalculateFieldNames.SM_HEADCOUNT_PERCENT]: sMHeadPercent,
      [CalculateFieldNames.RD_HEADCOUNT_PERCENT]: rDHeadPercent
    },
    handlers: {
      [CalculateFieldNames.ARR_PER_FTE]: onArrPerFTEChange,
      [CalculateFieldNames.CURRENT_ARR]: onCurrentArrChange,
      [CalculateFieldNames.TOTAL_HEADCOUNT]: onTotalHeadcountChange,
      [CalculateFieldNames.RULE_OF_40]: onRuleOf40Change,
      [CalculateFieldNames.FCF_MARGIN]: onFcfMarginChange,
      [CalculateFieldNames.AVG_DEAL_SIZE]: onAvgDealSizeChange,
      [CalculateFieldNames.YOY_ARR]: onYoyARRChange,
      [CalculateFieldNames.NEW_LOGO_ARR_OF_GROSS_NEW]:
        onNewLogoArrOfGrossNewChange,
      [CalculateFieldNames.EXPANSION_ARR_OF_GROSS_NEW]:
        onExpansionArrOfGrossNewChange,
      [CalculateFieldNames.NEW_LOGO_ARR]: onNewLogoArrChange,
      [CalculateFieldNames.EXPANSION_ARR]: onExpansionArrChange,
      [CalculateFieldNames.QUICK_RATIO]: onQuickRatioChange,
      [CalculateFieldNames.GROSS_CHURN_ARR]: onGrossChurnArrChange,
      [CalculateFieldNames.NET_DOLLAR_RETENTION]: onNetDollarRetentionChange,
      [CalculateFieldNames.BOP_ARR]: onBOPArrChange,
      [CalculateFieldNames.EOP_ARR]: onEOPArrChange,
      [CalculateFieldNames.NET_NEW_ARR]: onNetNewArrChange,
      [CalculateFieldNames.GROSS_DOLLAR_RETENTION]:
        onGrossDollarRetentionChange,
      [CalculateFieldNames.BURN_MULTIPLE]: onBurnMultipleChange,
      [CalculateFieldNames.NET_MAGIC_NUMBER]: onNetMagicNumberChange,
      [CalculateFieldNames.FCF]: onFCFChange,
      [CalculateFieldNames.SM_OPEX]: onSMOPEXChange,
      [CalculateFieldNames.OPEX_PER_FTE]: onOpexPerFTEChange,
      [CalculateFieldNames.TOTAL_OPEX]: onTotalOPEXChange,
      [CalculateFieldNames.RD_HEADCOUNT]: onRDHeadChange,
      [CalculateFieldNames.GA_HEADCOUNT]: onGAHeadChange,
      [CalculateFieldNames.SM_HEADCOUNT]: onSMHeadChange,
      [CalculateFieldNames.GA_HEADCOUNT_PERCENT]: onGAHeadPercentChange,
      [CalculateFieldNames.SM_HEADCOUNT_PERCENT]: onSMHeadPercentChange,
      [CalculateFieldNames.RD_HEADCOUNT_PERCENT]: onRDHeadPercentChange
    }
  };

  const handleCalculateBtnClick = (fieldName: CalculateFieldValue) => {
    const config = calculateModalConfigs[fieldName];

    if (!config) {
      console.error('Unknown field name:', fieldName);
      return;
    }

    setModalConfig({
      ...config,
      statesAndHandlers: calculateStatesAndHandlers
    });
    setIsCalculateModalOpen(true);
  };

  useEffect(() => {
    if (
      !revenue &&
      !currentARR &&
      !totalHeadcount &&
      !fcfMargin &&
      !yoyARR &&
      !totalOPEX
    ) {
      return;
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    Object.values(stateFieldMappings).forEach(fieldName => {
      const stateValue = calculateStatesAndHandlers.states[fieldName];
      if (stateValue !== null) {
        const updatedStates = updateDependentFields(
          fieldName,
          calculateStatesAndHandlers.states
        );

        Object.entries(updatedStates).forEach(([field, value]) => {
          if (
            value !== calculateStatesAndHandlers.states[field] &&
            value !== null
          ) {
            calculateStatesAndHandlers.handlers[field]?.(value);
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    revenue,
    currentARR,
    totalHeadcount,
    fcfMargin,
    yoyARR,
    totalOPEX,
    updateDependentFields
  ]);

  const onCalculateModalClose = () => {
    setIsCalculateModalOpen(false);
    setModalConfig(null);
  };

  const onClearForm = () => {
    setConfirmationModalOpen(true);
  };

  const onConfirmationModalClose = () => {
    setConfirmationModalOpen(false);
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const isFirstTimeModalOpen =
      localStorage.getItem('skip_data_modal_tutorial') !== 'true';
    const isFirstTimeNonPortcoModalOpen =
      localStorage.getItem('skip_data_modal_non_portco_tutorial') !== 'true';

    if (
      isOpen &&
      scrollDisabled &&
      mode === modalModes.ADD &&
      (isFirstTimeModalOpen ||
        (isAuthenticated && isFirstTimeNonPortcoModalOpen))
    ) {
      timeout = setTimeout(() => {
        setWalkthroughActive(true);
      }, 300);
    } else {
      setWalkthroughActive(false);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isOpen, mode, isAuthenticated, scrollDisabled]);

  const handleWalkthroughCallback = (data: CallBackProps) => {
    if (data.step && typeof data.step.target === 'string') {
      applySpotlightStyles(data.step.target as string);
    }

    if (data.action === ACTIONS.SKIP) {
      localStorage.setItem('skip_data_modal_tutorial', 'true');

      if (isAuthenticated) {
        localStorage.setItem('skip_data_modal_non_portco_tutorial', 'true');
      }
    }
    if (data.status === STATUS.FINISHED) {
      localStorage.setItem('skip_data_modal_tutorial', 'true');
      onSectionSelect(modalSections.REQUIRED_INFORMATION);

      if (isAuthenticated) {
        localStorage.setItem('skip_data_modal_non_portco_tutorial', 'true');
      }
    }
  };

  // Disable scrolling for the entire app during walkthrough
  useEffect(() => {
    if (isOpen) {
      const originalBodyStyle = document.body.style.overflow;
      const originalHtmlStyle = document.documentElement.style.overflow;

      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      setScrollDisabled(true);

      return () => {
        document.body.style.overflow = originalBodyStyle;
        document.documentElement.style.overflow = originalHtmlStyle;
        setScrollDisabled(false);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    window.onbeforeunload = e => {
      if (isOpen && isFormDirty) {
        e.preventDefault();
      }
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, [isOpen, isFormDirty]);

  const isDirty = useCallback(() => {
    if (mode === modalModes.EDIT) {
      return (
        currentCompanyStatus !== companyInfo.requiredInfo.status ||
        Number(currentARR) !== companyInfo.requiredInfo.currentARR ||
        Number(yoyARR) !== companyInfo.requiredInfo.yoyARR ||
        Number(fcfMargin) !== companyInfo.requiredInfo.fcfMargin ||
        Number(avgDealSize) !== companyInfo.requiredInfo.avgDealSize ||
        Number(totalHeadcount) !== companyInfo.requiredInfo.totalHeadcount ||
        companyWebsite !== companyInfo.companyDetails.companyWebsite ||
        Number(grossMargin) !== companyInfo.requiredInfo.grossMargin ||
        Number(netDollarRetention) !==
          companyInfo.growthAndRetention.netDollarRetention ||
        Number(netMagicNumber) !==
          companyInfo.operationalEfficiency.netMagicNumber ||
        Number(burnMultiple) !==
          companyInfo.operationalEfficiency.burnMultiple ||
        Number(quickRatio) !== companyInfo.growthAndRetention.quickRatio ||
        color !== companyInfo.companyDetails.companyColor ||
        Number(rD) !== companyInfo.operationalEfficiency.rD ||
        Number(gA) !== companyInfo.operationalEfficiency.gA ||
        Number(sM) !== companyInfo.operationalEfficiency.sM ||
        Number(rDHead) !== companyInfo.headCount.rDHead ||
        Number(gAHead) !== companyInfo.headCount.gAHead ||
        Number(sMHead) !== companyInfo.headCount.sMHead ||
        companyLogoURL !== companyInfo.companyDetails.companyLogoURL ||
        email !== companyInfo.email ||
        Number(revenue) !== companyInfo.requiredInfo.revenue ||
        Number(arrPerFTE) !== companyInfo.requiredInfo.arrPerFTE ||
        Number(ruleOf40) !== companyInfo.requiredInfo.ruleOf40 ||
        Number(newLogoArrOfGrossNew) !==
          companyInfo.growthAndRetention.newLogoArrOfGrossNew ||
        Number(expansionArrOfGrossNew) !==
          companyInfo.growthAndRetention.expansionArrOfGrossNew ||
        Number(grossDollarRetention) !==
          companyInfo.growthAndRetention.grossDollarRetention ||
        Number(newLogoVelocity) !==
          companyInfo.growthAndRetention.newLogoVelocity ||
        Number(CACPaybackPeriod) !==
          companyInfo.operationalEfficiency.cacPaybackPeriod ||
        Number(opexPerFTE) !== companyInfo.headCount.opexPerFTE ||
        Number(rdPercent) !== companyInfo.operationalEfficiency.rdPercent ||
        Number(gaPercent) !== companyInfo.operationalEfficiency.gaPercent ||
        Number(smPercent) !== companyInfo.operationalEfficiency.smPercent ||
        Number(totalOPEX) !== companyInfo.operationalEfficiency.totalOpex
      );
    } else {
      return (
        !!currentCompanyStatus ||
        !!currentARR ||
        !!yoyARR ||
        !!fcfMargin ||
        !!avgDealSize ||
        !!totalHeadcount ||
        !!companyWebsite ||
        !!grossMargin ||
        !!netDollarRetention ||
        !!netMagicNumber ||
        !!burnMultiple ||
        !!quickRatio ||
        !!color ||
        !!rD ||
        !!gA ||
        !!sM ||
        !!rDHead ||
        !!gAHead ||
        !!sMHead ||
        !!companyLogoURL ||
        !!email ||
        !!revenue ||
        !!arrPerFTE ||
        !!ruleOf40 ||
        !!newLogoArrOfGrossNew ||
        !!expansionArrOfGrossNew ||
        !!grossDollarRetention ||
        !!newLogoVelocity ||
        !!CACPaybackPeriod ||
        !!opexPerFTE ||
        !!rdPercent ||
        !!gaPercent ||
        !!smPercent ||
        !!totalOPEX
      );
    }
  }, [
    mode,
    companyInfo,
    currentCompanyStatus,
    currentARR,
    yoyARR,
    fcfMargin,
    avgDealSize,
    totalHeadcount,
    companyWebsite,
    grossMargin,
    netDollarRetention,
    netMagicNumber,
    burnMultiple,
    quickRatio,
    rD,
    gA,
    sM,
    rDHead,
    gAHead,
    sMHead,
    companyLogoURL,
    email,
    revenue,
    arrPerFTE,
    ruleOf40,
    newLogoArrOfGrossNew,
    expansionArrOfGrossNew,
    grossDollarRetention,
    newLogoVelocity,
    CACPaybackPeriod,
    opexPerFTE,
    color,
    rdPercent,
    gaPercent,
    smPercent,
    totalOPEX
  ]);

  useEffect(() => {
    setIsFormDirty(isDirty());
  }, [isDirty]);

  useEffect(() => {
    if (companyDetailsInView) {
      setSelected(modalSections.COMPANY_DETAILS);
    } else if (headcountInView) {
      setSelected(modalSections.HEADCOUNT);
    } else if (operationalEfficiencyInView) {
      setSelected(modalSections.OPERATIONAL_EFFICIENCY);
    } else if (growthAndRetentionInView) {
      setSelected(modalSections.GROWTH_AND_RETENTION);
    } else if (requiredInfoInView) {
      setSelected(modalSections.REQUIRED_INFORMATION);
    }
  }, [
    requiredInfoInView,
    growthAndRetentionInView,
    operationalEfficiencyInView,
    headcountInView,
    companyDetailsInView
  ]);

  useEffect(() => {
    if (mode === modalModes.EDIT) {
      setCompanyStatus(
        companyInfo.requiredInfo.status ? companyInfo.requiredInfo.status : null
      );
      setCurrentARR(
        companyInfo.requiredInfo.currentARR !== null
          ? String(companyInfo.requiredInfo.currentARR)
          : null
      );
      setRevenue(
        companyInfo.requiredInfo.revenue !== null
          ? String(companyInfo.requiredInfo.revenue)
          : null
      );
      setYoyARR(
        companyInfo.requiredInfo.yoyARR !== null
          ? String(companyInfo.requiredInfo.yoyARR)
          : null
      );
      setFcfMargin(
        companyInfo.requiredInfo.fcfMargin !== null
          ? String(companyInfo.requiredInfo.fcfMargin)
          : null
      );
      setAvgDealSize(
        companyInfo.requiredInfo.avgDealSize !== null
          ? String(companyInfo.requiredInfo.avgDealSize)
          : null
      );
      setTotalHeadcount(
        companyInfo.requiredInfo.totalHeadcount !== null
          ? String(companyInfo.requiredInfo.totalHeadcount)
          : null
      );
      setCompanyWebsite(companyInfo.companyDetails.companyWebsite);
      setColor(companyInfo.companyDetails.companyColor);
      setGrossMargin(
        companyInfo.requiredInfo.grossMargin !== null
          ? String(companyInfo.requiredInfo.grossMargin)
          : null
      );
      setArrPerFTE(
        companyInfo.requiredInfo.arrPerFTE !== null
          ? String(companyInfo.requiredInfo.arrPerFTE)
          : null
      );
      setRuleOf40(
        companyInfo.requiredInfo.ruleOf40 !== null
          ? String(companyInfo.requiredInfo.ruleOf40)
          : null
      );
      setNewLogoArrOfGrossNew(
        companyInfo.growthAndRetention.newLogoArrOfGrossNew !== null
          ? String(companyInfo.growthAndRetention.newLogoArrOfGrossNew)
          : null
      );
      setExpansionArrOfGrossNew(
        companyInfo.growthAndRetention.expansionArrOfGrossNew !== null
          ? String(companyInfo.growthAndRetention.expansionArrOfGrossNew)
          : null
      );
      setNetDollarRetention(
        companyInfo.growthAndRetention.netDollarRetention !== null
          ? String(companyInfo.growthAndRetention.netDollarRetention)
          : null
      );
      setGrossDollarRetention(
        companyInfo.growthAndRetention.grossDollarRetention !== null
          ? String(companyInfo.growthAndRetention.grossDollarRetention)
          : null
      );
      setNewLogoVelocity(
        companyInfo.growthAndRetention.newLogoVelocity !== null
          ? String(companyInfo.growthAndRetention.newLogoVelocity)
          : null
      );
      setNetMagicNumber(
        companyInfo.operationalEfficiency.netMagicNumber !== null
          ? String(companyInfo.operationalEfficiency.netMagicNumber)
          : null
      );
      setBurnMultiple(
        companyInfo.operationalEfficiency.burnMultiple !== null
          ? String(companyInfo.operationalEfficiency.burnMultiple)
          : null
      );
      setQuickRatio(
        companyInfo.growthAndRetention.quickRatio !== null
          ? String(companyInfo.growthAndRetention.quickRatio)
          : null
      );
      setRD(
        companyInfo.operationalEfficiency.rD !== null
          ? String(companyInfo.operationalEfficiency.rD)
          : null
      );
      setGA(
        companyInfo.operationalEfficiency.gA !== null
          ? String(companyInfo.operationalEfficiency.gA)
          : null
      );
      setSM(
        companyInfo.operationalEfficiency.sM !== null
          ? String(companyInfo.operationalEfficiency.sM)
          : null
      );
      setTotalOPEX(
        companyInfo.operationalEfficiency.totalOpex !== null
          ? String(companyInfo.operationalEfficiency.totalOpex)
          : null
      );
      setRDPercent(
        companyInfo.operationalEfficiency.rdPercent !== null
          ? String(companyInfo.operationalEfficiency.rdPercent)
          : null
      );
      setGAPercent(
        companyInfo.operationalEfficiency.gaPercent !== null
          ? String(companyInfo.operationalEfficiency.gaPercent)
          : null
      );
      setSMPercent(
        companyInfo.operationalEfficiency.smPercent !== null
          ? String(companyInfo.operationalEfficiency.smPercent)
          : null
      );
      setCACPaybackPeriod(
        companyInfo.operationalEfficiency.cacPaybackPeriod !== null
          ? String(companyInfo.operationalEfficiency.cacPaybackPeriod)
          : null
      );
      setOpexPerFTE(
        companyInfo.headCount.opexPerFTE !== null
          ? String(companyInfo.headCount.opexPerFTE)
          : null
      );
      setRDHead(
        companyInfo.headCount.rDHead !== null
          ? String(companyInfo.headCount.rDHead)
          : null
      );
      setGAHead(
        companyInfo.headCount.gAHead !== null
          ? String(companyInfo.headCount.gAHead)
          : null
      );
      setSMHead(
        companyInfo.headCount.sMHead !== null
          ? String(companyInfo.headCount.sMHead)
          : null
      );
      setRDHeadPercent(
        companyInfo.headCount.rDHeadPercent !== null
          ? String(companyInfo.headCount.rDHeadPercent)
          : null
      );
      setGAHeadPercent(
        companyInfo.headCount.gAHeadPercent !== null
          ? String(companyInfo.headCount.gAHeadPercent)
          : null
      );
      setSMHeadPercent(
        companyInfo.headCount.sMHeadPercent !== null
          ? String(companyInfo.headCount.sMHeadPercent)
          : null
      );
      setCompanyLogoURL(
        companyInfo.companyDetails.companyLogoURL !== null
          ? companyInfo.companyDetails.companyLogoURL
          : null
      );
      setEmail(companyInfo.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const dispatch = useAppDispatch();

  const isRequiredEmpty = () => {
    return (
      !currentCompanyStatus ||
      !revenue ||
      !fcfMargin ||
      !avgDealSize ||
      !totalHeadcount ||
      !grossMargin ||
      !yoyARR ||
      !arrPerFTE ||
      !ruleOf40 ||
      (gAHead && (!sMHead || !rDHead)) ||
      (sMHead && (!gAHead || !rDHead)) ||
      (rDHead && (!sMHead || !gAHead)) ||
      ((!!rDHead || !!sMHead || !!gAHead) &&
        Number(totalHeadcount) !==
          Number(gAHead) + Number(sMHead) + Number(rDHead)) ||
      (currentCompanyStatus === companyStatus.PRIVATE &&
        !isEmail(String(email)))
    );
  };

  const isApplyDisabled = () => {
    return isRequiredEmpty();
  };

  const isUpdateDisabled = () => {
    if (
      (currentCompanyStatus === companyInfo.requiredInfo.status &&
        Number(currentARR) === companyInfo.requiredInfo.currentARR &&
        Number(yoyARR) === companyInfo.requiredInfo.yoyARR &&
        Number(fcfMargin) === companyInfo.requiredInfo.fcfMargin &&
        Number(avgDealSize) === companyInfo.requiredInfo.avgDealSize &&
        Number(totalHeadcount) === companyInfo.requiredInfo.totalHeadcount &&
        companyWebsite === companyInfo.companyDetails.companyWebsite &&
        Number(grossMargin) === companyInfo.requiredInfo.grossMargin &&
        Number(netDollarRetention) ===
          companyInfo.growthAndRetention.netDollarRetention &&
        Number(netMagicNumber) ===
          companyInfo.operationalEfficiency.netMagicNumber &&
        Number(burnMultiple) ===
          companyInfo.operationalEfficiency.burnMultiple &&
        Number(quickRatio) === companyInfo.growthAndRetention.quickRatio &&
        color === companyInfo.companyDetails.companyColor &&
        Number(rD) === companyInfo.operationalEfficiency.rD &&
        Number(gA) === companyInfo.operationalEfficiency.gA &&
        Number(sM) === companyInfo.operationalEfficiency.sM &&
        Number(rDHead) === companyInfo.headCount.rDHead &&
        Number(gAHead) === companyInfo.headCount.gAHead &&
        Number(sMHead) === companyInfo.headCount.sMHead &&
        companyLogoURL === companyInfo.companyDetails.companyLogoURL &&
        email === companyInfo.email &&
        Number(revenue) === companyInfo.requiredInfo.revenue &&
        Number(arrPerFTE) === companyInfo.requiredInfo.arrPerFTE &&
        Number(ruleOf40) === companyInfo.requiredInfo.ruleOf40 &&
        Number(newLogoArrOfGrossNew) ===
          companyInfo.growthAndRetention.newLogoArrOfGrossNew &&
        Number(expansionArrOfGrossNew) ===
          companyInfo.growthAndRetention.expansionArrOfGrossNew &&
        Number(grossDollarRetention) ===
          companyInfo.growthAndRetention.grossDollarRetention &&
        Number(newLogoVelocity) ===
          companyInfo.growthAndRetention.newLogoVelocity &&
        Number(CACPaybackPeriod) ===
          companyInfo.operationalEfficiency.cacPaybackPeriod &&
        Number(opexPerFTE) === companyInfo.headCount.opexPerFTE &&
        Number(rdPercent) === companyInfo.operationalEfficiency.rdPercent &&
        Number(gaPercent) === companyInfo.operationalEfficiency.gaPercent &&
        Number(smPercent) === companyInfo.operationalEfficiency.smPercent &&
        Number(totalOPEX) === companyInfo.operationalEfficiency.totalOpex) ||
      isRequiredEmpty()
    ) {
      return true;
    }
    return false;
  };

  const buildLocalData = (): ISubmitData<string> => ({
    // Required Info
    companyStatus: currentCompanyStatus,
    currentARR: currentARR,
    revenue: revenue,
    fcfMargin: fcfMargin,
    avgDealSize: avgDealSize,
    totalHeadcount: totalHeadcount,
    grossMargin: grossMargin,
    yoyARR: yoyARR,
    arrPerFTE: arrPerFTE,
    ruleOf40: ruleOf40,

    // Growth and Retention
    newLogoARR: newLogoArr,
    expansionARR: expansionArr,
    newLogoArrOfGrossNew: newLogoArrOfGrossNew,
    expansionArrOfGrossNew: expansionArrOfGrossNew,
    quickRatio: quickRatio,
    netDollarRetention: netDollarRetention,
    grossDollarRetention: grossDollarRetention,
    newLogoVelocity: newLogoVelocity,

    // Operational Efficiency
    burnMultiple: burnMultiple,
    netMagicNumber: netMagicNumber,
    rD: rD,
    gA: gA,
    sM: sM,
    rDPercent: rdPercent,
    gAPercent: gaPercent,
    sMPercent: smPercent,
    cacPayback: CACPaybackPeriod,

    // Headcount
    opexPerFTE: opexPerFTE,
    rDHead: rDHead,
    gAHead: gAHead,
    sMHead: sMHead,
    rDHeadPercent: rDHeadPercent,
    gAHeadPercent: gAHeadPercent,
    sMHeadPercent: sMHeadPercent,

    // Company Details
    companyLogoURL: companyLogoURL || null,
    companyColor: color,
    companyWebsite,
    email: email || '',

    // Additional fields
    grossChurnARR: grossChurnArr,
    beginPeriodARR: BOPArr,
    endPeriodARR: eopArr,
    netNewARR: netNewArr,
    sMOPEX: smOPEX,
    totalOPEX: totalOPEX,
    uuid: sessionStorage.getItem('uuid') || localStorage.getItem('uuid') || ''
  });

  const onApply = async () => {
    const localData = buildLocalData();
    await submitData(localData, onClose, { email });
  };

  const onUpdate = async () => {
    const localData = buildLocalData();
    const additionalData = {
      isUpdated: true,
      url: window.location.href,
      filtersSoftwareSector: softwareSector.map(item => item.value),
      filtersGrowthMotion: growthMotion.map(item => item.value),
      filtersCustomerSegment: primaryCustomer.map(item => item.value),
      email: email || ''
    };

    await submitData(localData, onClose, additionalData);
  };

  const clearData = () => {
    dispatch(resetCompanyInfo());
    dispatch(resetChartForms());
    setCompanyStatus(null);
    setCurrentARR(null);
    setRevenue(null);
    setFcfMargin(null);
    setAvgDealSize(null);
    setTotalHeadcount(null);
    setGrossMargin(null);
    setYoyARR(null);
    setArrPerFTE(null);
    setRuleOf40(null);
    setNewLogoArrOfGrossNew(null);
    setExpansionArrOfGrossNew(null);
    setQuickRatio(null);
    setNetDollarRetention(null);
    setGrossDollarRetention(null);
    setNewLogoVelocity(null);
    setBurnMultiple(null);
    setNetMagicNumber(null);
    setGA(null);
    setSM(null);
    setRD(null);
    setCACPaybackPeriod(null);
    setOpexPerFTE(null);
    setGAHead(null);
    setSMHead(null);
    setRDHead(null);
    setGAHeadPercent(null);
    setSMHeadPercent(null);
    setRDHeadPercent(null);
    setNewLogoArr(null);
    setExpansionArr(null);
    setGrossChurnArr(null);
    setBOPArr(null);
    setEOPArr(null);
    setNetNewArr(null);
    setTotalOPEX(null);
    setSMOPEX(null);
    setCompanyWebsite('');
    setColor('');
    setCompanyLogoURL(null);
    setEmail('');
    onGrowthEnterpriseReset();
    onConfirmationModalClose();
  };

  const onReset = () => {
    clearData();
    onClose();
  };

  useEffect(() => {
    const fetchHistoricalFormData = async () => {
      try {
        setIsLoading(true);
        const data = await getHistoricalCompsData();
        setUserHistoricalFormData(data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching historical comps data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && isAuthenticated && isPortCo === false)
      fetchHistoricalFormData();
  }, [isOpen, isAuthenticated, isPortCo]);

  const handleSavedDataSelect = (entry: UserHistoricalFormDataItem) => {
    setSelectedSavedData(entry);
  };

  const handleSavedDataClose = () => {
    setSelectedSavedData(null);
  };

  const handleSavedDataDelete = () => {
    setSelectedSavedData(null);
    clearData();
  };

  const handleSavedDataApply = (data: UserHistoricalFormDataItem) => {
    const companyData = data;

    if (!companyData) {
      console.error('No company data found in saved entry');
      return;
    }

    // Required Info fields
    setCompanyStatus(companyData.STATUS as companyStatus);
    setCurrentARR(companyData.CURRENT_ARR_OR_ANNUALIZED_REVENUE || null);
    setRevenue(companyData.REVENUE || null);
    setFcfMargin(companyData.FCF_MARGIN || null);
    setAvgDealSize(companyData.AVGDEALSIZE || null);
    setTotalHeadcount(companyData.TOTAL_HEADCOUNT || null);
    setGrossMargin(companyData.GROSS_MARGIN || null);
    setYoyARR(companyData.YOY_ARR_OR_REVENUE_GROWTH || null);
    setArrPerFTE(companyData.ARR_PER_FTE || null);
    setRuleOf40(companyData.RULE_OF_40 || null);

    // Growth and Retention fields
    setNewLogoArrOfGrossNew(companyData.NEWLOGOARROFGROSSNEW || null);
    setExpansionArrOfGrossNew(companyData.EXPANSIONARROFGROSSNEW || null);
    setQuickRatio(companyData.QUICK_RATIO || null);
    setNetDollarRetention(companyData.NET_DOLLAR_RETENTION || null);
    setGrossDollarRetention(companyData.GROSSDOLLARRETENTION || null);
    setNewLogoVelocity(companyData.NEWLOGOVELOCITY || null);
    setNewLogoArr(companyData.NEWLOGOARR || null);
    setExpansionArr(companyData.EXPANSIONARR || null);

    // Operational Efficiency fields
    setBurnMultiple(companyData.BURN_MULTIPLE || null);
    setNetMagicNumber(companyData.NET_MAGIC_NUMBER || null);
    setRD(companyData.OPEX_FUNCTION_AS_A_PERCENTAGE_OF_REVENUE_R_AND_D || null);
    setGA(companyData.OPEX_FUNCTION_AS_A_PERCENTAGE_OF_REVENUE_G_AND_A || null);
    setSM(companyData.OPEX_FUNCTION_AS_A_PERCENTAGE_OF_REVENUE_S_AND_M || null);
    setCACPaybackPeriod(companyData.CACPAYBACK || null);
    setRDPercent(companyData.RDPERCENT || null);
    setGAPercent(companyData.GAPERCENT || null);
    setSMPercent(companyData.SMPERCENT || null);
    setTotalOPEX(companyData.TOTALOPEX || null);

    // Headcount fields
    setOpexPerFTE(companyData.OPEXPERFTE || null);
    setRDHead(companyData.HEADCOUNT_R_AND_D || null);
    setGAHead(companyData.HEADCOUNT_G_AND_A || null);
    setSMHead(companyData.HEADCOUNT_S_AND_M || null);
    setRDHeadPercent(companyData.HEADCOUNT_PER_R_AND_D || null);
    setGAHeadPercent(companyData.HEADCOUNT_PER_G_AND_A || null);
    setSMHeadPercent(companyData.HEADCOUNT_PER_S_AND_M || null);

    // Company Details fields
    setCompanyLogoURL(companyData.COMPANY_LOGO);
    setColor(companyData.COMPANY_COLOR);
    setCompanyWebsite(companyData.COMPANY_WEBSITE);
    setEmail(companyData.EMAIL || '');

    setSelectedTabs(modalTabs.FORM);
    setSelectedSavedData(null);
  };

  useEffect(() => {
    if (isAuthenticated) {
      setEmail(user?.idTokenClaims?.emails?.[0] || '');
    }
  }, [user, isAuthenticated]);

  return (
    <>
      {selectedSavedData ? (
        <CustomModal isOpen={isOpen} onClose={onClose} className='rounded-lg'>
          <SavedDataDetails
            data={selectedSavedData}
            onClose={handleSavedDataClose}
            onDelete={handleSavedDataDelete}
            onApply={handleSavedDataApply}
          />
        </CustomModal>
      ) : (
        <CustomModal
          isOpen={isOpen}
          onClose={onClose}
          className='w-[960px] h-[95dvh] 2xl:h-[730px] rounded-lg'
        >
          <div className='w-full h-full overflow-hidden relative flex flex-col items-center'>
            <Header
              mode={mode}
              onClose={onClose}
              selectedTabs={selectedTabs}
              setSelectedTabs={setSelectedTabs}
            />
            {selectedTabs === modalTabs.FORM ? (
              <div className='w-full h-full relative overflow-hidden flex flex-row'>
                <Sidebar
                  selected={selected}
                  onSectionSelect={onSectionSelect}
                />
                <div className='w-[calc(100%-204px)] flex flex-col items-start gap-8 px-8 py-6 overflow-auto'>
                  <RequiredInfoSection
                    requiredInfoParentRef={requiredInfoParentRef}
                    requiredInfoRef={requiredInfoRef}
                    currentCompanyStatus={currentCompanyStatus}
                    onCompanyStatusChange={onCompanyStatusChange}
                    currentARR={currentARR}
                    onCurrentArrChange={onCurrentArrChange}
                    revenue={revenue}
                    onRevenueChange={onRevenueChange}
                    totalHeadcount={totalHeadcount}
                    onTotalHeadcountChange={onTotalHeadcountChange}
                    fcfMargin={fcfMargin}
                    onFcfMarginChange={onFcfMarginChange}
                    grossMargin={grossMargin}
                    onGrossMarginChange={onGrossMarginChange}
                    yoyARR={yoyARR}
                    onYoyARRChange={onYoyARRChange}
                    arrPerFTE={arrPerFTE}
                    onArrPerFTEChange={onArrPerFTEChange}
                    ruleOf40={ruleOf40}
                    onRuleOf40Change={onRuleOf40Change}
                    avgDealSize={avgDealSize}
                    onAvgDealSizeChange={onAvgDealSizeChange}
                    handleCalculateBtnClick={handleCalculateBtnClick}
                  />
                  <GrowthAndRetentionSection
                    growthAndRetentionParentRef={growthAndRetentionParentRef}
                    growthAndRetentionRef={growthAndRetentionRef}
                    newLogoArrOfGrossNew={newLogoArrOfGrossNew}
                    onNewLogoArrOfGrossNewChange={onNewLogoArrOfGrossNewChange}
                    expansionArrOfGrossNew={expansionArrOfGrossNew}
                    onExpansionArrOfGrossNewChange={
                      onExpansionArrOfGrossNewChange
                    }
                    grossDollarRetention={grossDollarRetention}
                    onGrossDollarRetentionChange={onGrossDollarRetentionChange}
                    netDollarRetention={netDollarRetention}
                    onNetDollarRetentionChange={onNetDollarRetentionChange}
                    quickRatio={quickRatio}
                    onQuickRatioChange={onQuickRatioChange}
                    newLogoVelocity={newLogoVelocity}
                    onNewLogoVelocityChange={onNewLogoVelocityChange}
                    handleCalculateBtnClick={handleCalculateBtnClick}
                  />
                  <OperationalEfficiencySection
                    operationalEfficiencyParentRef={
                      operationalEfficiencyParentRef
                    }
                    operationalEfficiencyRef={operationalEfficiencyRef}
                    burnMultiple={burnMultiple}
                    onBurnMultipleChange={onBurnMultipleChange}
                    fcfMargin={fcfMargin}
                    netMagicNumber={netMagicNumber}
                    onNetMagicNumberChange={onNetMagicNumberChange}
                    revenue={revenue}
                    gA={gA}
                    onGAChange={onGAChange}
                    gaPercent={gaPercent}
                    onGAPercentChange={onGAPercentChange}
                    sM={sM}
                    onSMChange={onSMChange}
                    smPercent={smPercent}
                    onSMPercentChange={onSMPercentChange}
                    rD={rD}
                    onRDChange={onRDChange}
                    rdPercent={rdPercent}
                    onRDPercentChange={onRDPercentChange}
                    CACPaybackPeriod={CACPaybackPeriod}
                    onCACPaybackPeriodChange={onCACPaybackPeriodChange}
                    handleCalculateBtnClick={handleCalculateBtnClick}
                  />
                  <HeadcountSection
                    headcountParentRef={headcountParentRef}
                    headcountRef={headcountRef}
                    opexPerFTE={opexPerFTE}
                    onOpexPerFTEChange={onOpexPerFTEChange}
                    gAHead={gAHead}
                    onGAHeadChange={onGAHeadChange}
                    sMHead={sMHead}
                    onSMHeadChange={onSMHeadChange}
                    rDHead={rDHead}
                    onRDHeadChange={onRDHeadChange}
                    totalHeadcount={totalHeadcount}
                    handleCalculateBtnClick={handleCalculateBtnClick}
                  />
                  <CompanyDetailsSection
                    companyDetailsParentRef={companyDetailsParentRef}
                    companyDetailsRef={companyDetailsRef}
                    companyLogoURL={companyLogoURL}
                    onImageUpload={onImageUpload}
                    fileInputRef={fileInputRef}
                    handleImageUploadInputClick={handleImageUploadInputClick}
                    invalidFileSize={invalidFileSize}
                    setCompanyLogoURL={setCompanyLogoURL}
                    color={color}
                    companyWebsite={companyWebsite}
                    onColorChange={onColorChange}
                    onCompanyWebsiteChange={onCompanyWebsiteChange}
                  />
                </div>
              </div>
            ) : (
              <SavedUserData
                userHistoricalFormData={userHistoricalFormData}
                isLoading={isLoading}
                onDataSelect={handleSavedDataSelect}
              />
            )}
            <Footer
              mode={mode}
              isApplyDisabled={isApplyDisabled}
              isUpdateDisabled={isUpdateDisabled}
              onApply={onApply}
              onUpdate={onUpdate}
              email={email}
              onChangeEmail={onChangeEmail}
              isSubmitting={isSubmitting}
              onClearForm={onClearForm}
            />
          </div>
        </CustomModal>
      )}
      <ConfirmationModal
        isOpen={confirmationModalOpen}
        onClose={onConfirmationModalClose}
        onReset={onReset}
      />
      <ReactJoyride
        run={walkthroughActive}
        steps={walkthroughSteps}
        callback={handleWalkthroughCallback}
        continuous
        disableOverlayClose
        tooltipComponent={CustomWalkthroughTooltip}
        scrollDuration={1000}
        locale={{ last: 'Finish' }}
        styles={{ options: { arrowColor: 'white' } }}
      />
      {modalConfig && (
        <CalculateModal
          isOpen={isCalculateModalOpen}
          onClose={onCalculateModalClose}
          config={modalConfig}
          updateDependentFields={updateDependentFields}
        />
      )}
    </>
  );
};

export default DataEntryModal;
